import { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { AnimatePresence } from 'framer-motion';

import { Button } from '@components/button';
import { isV2Form } from '@components/form-builder';
import { FullscreenPreview } from '@components/form-builder/fullscreen-preview';
import { mergePredefinedOptions } from '@components/form-builder/utils';
import { IconButton } from '@components/icon-button';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconOpenPreview } from '@components/icons/open-preview';
import { useScopedTranslate } from '@locale/translate';

import { useForm, useFormTemplate } from 'api/forms/hooks';
import { FormTemplateBase } from 'api/forms/requests';
import { useFormBuilderLists } from 'api/lists/hooks';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';
import { isInTemplateRoute } from 'ui/router';

import { TEMPLATE_PREVIEW_BUTTON_CLASSNAME } from '../common';

type FormPreviewProps = {
  shortId: FormTemplateBase['shortId'];
  onClose(): void;
};

const FormTemplatePreview = ({ shortId, onClose }: FormPreviewProps) => {
  const { t } = useScopedTranslate(locale);
  const { workspaceId } = useCurrentWorkspace();
  const { data: lists } = useFormBuilderLists();
  const formTemplateQuery = useFormTemplate(shortId);
  const isJourneyTemplateView = isInTemplateRoute();
  if (!formTemplateQuery.isSuccess) return null;

  const form = formTemplateQuery.data.fieldsConfig;
  if (!isV2Form(form)) return null;

  const fields = mergePredefinedOptions(form.fields, lists);
  const name = formTemplateQuery.data.name;

  const builderPath = generatePath(routePaths.workspaceFormTemplatesEdit, {
    workspaceId,
    shortId,
    '*': '',
  });

  return (
    <FullscreenPreview onClose={onClose} name={name} fields={fields}>
      {!isJourneyTemplateView && (
        <Button
          variant="secondary"
          iconLeft={<IconOpenNewTab />}
          text={t('openInBuilder')}
          href={builderPath}
          target="_blank"
        />
      )}
    </FullscreenPreview>
  );
};

const FormPreview = ({ shortId, onClose }: FormPreviewProps) => {
  const { data: lists } = useFormBuilderLists();
  const formQuery = useForm(shortId);
  if (!formQuery.isSuccess) return null;

  const form = formQuery.data.fieldsConfig;
  if (!isV2Form(form)) return null;

  const fields = mergePredefinedOptions(form.fields, lists);
  const name = formQuery.data.name;

  return <FullscreenPreview onClose={onClose} name={name} fields={fields} />;
};

export type IsTemplateProp = {
  isTemplate?: boolean;
};

type FormPreviewHandlerProps = FormPreviewProps & IsTemplateProp & { isOpen: boolean };

const FormPreviewHandler = ({ isTemplate = false, isOpen, ...rest }: FormPreviewHandlerProps) => {
  if (!isOpen) {
    return null;
  }
  const PreviewComponent = isTemplate ? FormTemplatePreview : FormPreview;
  return (
    <AnimatePresence>
      <PreviewComponent {...rest} />{' '}
    </AnimatePresence>
  );
};

type FormPreviewButtonProps = Pick<FormPreviewProps, 'shortId'> & IsTemplateProp;

export const FormPreviewButton = (props: FormPreviewButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <FormPreviewHandler isOpen={isOpen} onClose={() => setIsOpen(false)} {...props} />
      <IconButton
        className={TEMPLATE_PREVIEW_BUTTON_CLASSNAME}
        dataTestid="preview"
        icon={<IconOpenPreview />}
        variant="ghost-purple"
        onClick={() => setIsOpen(true)}
      />
    </>
  );
};

const locale = {
  en: {
    openInBuilder: 'Open in form builder',
  },
};
